// components/analytics/posthog-identify-bridge.tsx — Phase 1 INFRA-11
//
// Calls posthog.identify(user.id) on auth completion to merge the anonymous
// session's distinct_id into the authed identity inside PostHog.
//
// We rely on identify() alone — NOT posthog.alias() — for the anon→authed
// merge. When the SDK currently holds an anonymous distinct_id, identify()
// emits an $identify carrying that id as $anon_distinct_id, and PostHog's
// ingest merges the anon person into the authed user. identify() is the
// supported merge path and is a safe no-op once the user is already identified.
//
// Why not alias(): posthog.alias(user.id, anonId) uses the authed user.id as
// the alias *target*. After the first identify, user.id is already an
// identified person, so every subsequent auth (return visit, cleared
// localStorage with a surviving anon cookie, a fresh anon id) made the SDK
// re-fire alias(user.id, …) → PostHog rejected it with the
// `cannot_merge_already_identified` ingestion warning. identify()'s
// anon→identified merge is accepted in those cases (the anon side is still
// anonymous), so the merge still happens without the warning.
//
// Mount point: app/(app)/layout.tsx, inside <PostHogProvider>, BEFORE other
// children.

"use client";

import { useEffect, useRef } from "react";
import { useClerk, useUser } from "@clerk/nextjs";
import posthog from "posthog-js";
import { readAnalyticsConsent } from "@/components/analytics/browser-attribution";
import { syncPostHogAnonCookieToSdk } from "@/components/analytics/posthog-provider";
import { subscribeToBrowserConsentCategory } from "@/components/analytics/browser-consent-lifecycle";
import {
  POSTHOG_BROWSER_INITIALIZED_EVENT,
  POSTHOG_IDENTITY_READY_EVENT,
} from "@/lib/analytics/posthog-browser-lifecycle";

/**
 * The caller's B2B account, resolved server-side in app/(app)/layout.tsx and
 * threaded down so the client can associate the person with the PostHog
 * "account" group (group analytics). Dates are pre-serialised to ISO strings
 * because this crosses the server→client boundary.
 */
export interface IdentifyBridgeAccount {
  id: string;
  plan: string;
  name: string | null;
  createdAt: string;
  stripeSubscriptionStatus: string | null;
}

function currentDistinctId(): string | null {
  try {
    return posthog.get_distinct_id();
  } catch {
    // A cleared/uninitialised persistence layer must read as "not this
    // user" so the bridge re-identifies rather than skipping.
    return null;
  }
}

/**
 * Reset a LEFTOVER identified person, deciding from SDK truth: this app only
 * ever identifies with Clerk ids (`user_…`), while PostHog's own anonymous
 * ids are UUIDs. Idempotent — a genuinely anonymous session is untouched.
 * After the reset the server-side ph_anon_id cookie is re-minted from the
 * SDK's fresh anonymous id so browser and server events keep stitching.
 */
function resetLeftoverIdentity(): boolean {
  if (!posthog.__loaded) return false;
  if (currentDistinctId()?.startsWith("user_") !== true) return false;
  posthog.reset();
  syncPostHogAnonCookieToSdk();
  return true;
}

export function PostHogIdentifyBridge({
  account,
}: {
  account?: IdentifyBridgeAccount;
}) {
  const { user, isLoaded } = useUser();
  const clerk = useClerk();
  const hasIdentified = useRef(false);
  // Identification, person properties and grouping are tracked SEPARATELY: a
  // new `account` prop (plan upgrade, subscription recovery, account creation
  // via router.refresh()) must refresh the group's properties, and a changed
  // primary email must reach the person — neither needs a re-identify.
  const groupedAccountSignature = useRef<string | null>(null);
  const identifiedEmail = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoaded) return;
    if (!user?.id) {
      // Loaded-but-absent means the user SIGNED OUT. PostHog's persisted
      // identity and account group would otherwise attribute the logged-out
      // pages — and the next user on a shared browser — to the previous
      // person and tenant. Decided from SDK truth, not the local ref:
      // Clerk's sign-out redirect can unmount the authed layout before this
      // effect observes the transition, so the FRESH (auth)-segment mount is
      // the backstop (the pre-navigation Clerk listener below is the primary
      // cleanup).
      resetLeftoverIdentity();
      hasIdentified.current = false;
      groupedAccountSignature.current = null;
      identifiedEmail.current = null;
      return;
    }

    const accountSignature = account
      ? JSON.stringify([
          account.id,
          account.plan,
          account.name,
          account.createdAt,
          account.stripeSubscriptionStatus,
        ])
      : null;

    function identifyIfReady() {
      if (
        !posthog.__loaded ||
        readAnalyticsConsent() !== "accepted" ||
        !user?.id ||
        // An opted-out singleton drops identify()/group() silently — setting
        // the guard then would strand the session anonymous. The readiness
        // re-emit after opt-in is the retry moment.
        posthog.has_opted_out_capturing?.()
      ) {
        return;
      }
      const email = user.primaryEmailAddress?.emailAddress ?? null;
      // hasIdentified is only an optimisation; the SDK's LIVE distinct id is
      // the truth. Suppression on a token URL (and a consent withdrawal)
      // clears PostHog's persisted person/group identity WITHOUT touching the
      // Analytics cookie or this flag — whenever the singleton no longer
      // carries this user, identify again.
      const identified =
        hasIdentified.current && currentDistinctId() === user.id;

      if (!identified) {
        // identify() merges the current anonymous distinct_id into user.id
        // (when the SDK is still anonymous) and no-ops once identified. No
        // alias() — see the file header for why that produced
        // cannot_merge_already_identified.
        posthog.identify(user.id, {
          email: email ?? undefined,
        });
        hasIdentified.current = true;
        identifiedEmail.current = email;
        // Whatever cleared the identity cleared the group link with it —
        // force the group call below to re-associate.
        groupedAccountSignature.current = null;
      } else if (identifiedEmail.current !== email) {
        // Same person, new primary email: update the person properties
        // WITHOUT identify()'s anonymous-merge semantics.
        posthog.setPersonProperties?.({ email: email ?? undefined });
        identifiedEmail.current = email;
      }

      // Associate this person with their account group so events captured
      // client side roll up into account-level analytics. Called after
      // identify() so the link attaches to the identified person; re-called
      // whenever the account's properties change so the group never carries
      // a stale plan/subscription status.
      if (account) {
        if (groupedAccountSignature.current !== accountSignature) {
          posthog.group("account", account.id, {
            plan: account.plan,
            name: account.name ?? "",
            createdAt: account.createdAt,
            stripeSubscriptionStatus: account.stripeSubscriptionStatus,
          });
        }
      } else {
        // Removed from their only account (membership revoked, accountless
        // onboarding): later client events must stop rolling up to the
        // former tenant. Read the SDK's PERSISTED groups, not the local ref —
        // a fresh mount after a reload starts with an empty ref while the
        // persistence layer can still carry the old tenant.
        const persistedGroups = posthog.getGroups?.() ?? {};
        if (Object.keys(persistedGroups).length > 0) {
          posthog.resetGroups?.();
        }
      }
      groupedAccountSignature.current = accountSignature;
      if (currentDistinctId() === user.id) {
        window.dispatchEvent(new CustomEvent(POSTHOG_IDENTITY_READY_EVENT));
      }
    }

    function onConsentChange() {
      // A withdrawal makes the provider opt the SDK out and clear its
      // identity/group persistence, so a LATER re-acceptance must identify
      // again — otherwise the rest of the session captures anonymously,
      // detached from the person and account.
      if (readAnalyticsConsent() !== "accepted") {
        hasIdentified.current = false;
        return;
      }
      // Let the provider apply the authoritative cookie to the SDK first.
      queueMicrotask(identifyIfReady);
    }

    identifyIfReady();

    // Stay subscribed for the whole mount — even after a successful identify —
    // so a withdraw→re-accept cycle inside one session re-identifies.
    window.addEventListener(POSTHOG_BROWSER_INITIALIZED_EVENT, identifyIfReady);
    const unsubscribeConsent = subscribeToBrowserConsentCategory(
      "analytics",
      onConsentChange,
    );
    return () => {
      window.removeEventListener(
        POSTHOG_BROWSER_INITIALIZED_EVENT,
        identifyIfReady,
      );
      unsubscribeConsent();
    };
  }, [isLoaded, user?.id, user?.primaryEmailAddress?.emailAddress, account]);

  useEffect(() => {
    // PRIMARY sign-out cleanup: Clerk emits the resource change the moment
    // the client session ends — BEFORE the redirect to afterSignOutUrl — so
    // the auth-route $pageview captured during that navigation belongs to
    // the fresh anonymous person, not the one who just signed out. The
    // signed-out branch of the effect above stays as the backstop for
    // mounts that happen after the redirect already landed.
    return clerk.addListener((resources) => {
      if (resources.user === null) {
        resetLeftoverIdentity();
        hasIdentified.current = false;
        groupedAccountSignature.current = null;
        identifiedEmail.current = null;
      }
    });
  }, [clerk]);

  return null;
}
